import React from "react";
import {
  Typography,
  Box,
  Divider,
  Stack,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Fade,
} from "@mui/material";
import CodeIcon from "@mui/icons-material/Code";
import UploadIcon from "@mui/icons-material/Upload";
import PersonIcon from "@mui/icons-material/Person";
import SendIcon from "@mui/icons-material/Send";
import logo from "../Image/9rlogo.png";

export default function AboutPage() {
  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 180px)",
        background: "linear-gradient(135deg, #e0e7ff 0%, #d1fae5 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        py: { xs: 2, md: 4 },
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Subtle Background Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          background: "radial-gradient(circle, rgba(102, 126, 234, 0.1) 0%, transparent 70%)",
          zIndex: 0,
          opacity: 0.5,
        }}
      />

      <Fade in timeout={1000}>
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            maxWidth: 950,
            mx: "auto",
            borderRadius: 2,
            background: "linear-gradient(135deg, #ffffff 0%, #f9fafb 100%)",
            border: "1px solid rgba(102, 126, 234, 0.2)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.05)",
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 3, md: 5 } }}>
            <Stack direction="row" spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 3 }}>
              <Box component="img" src={logo} alt="Logo" sx={{ height: 40, width: 40, mr: 1.5 }} />
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "#1f2937",
                  letterSpacing: 0.5,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: { xs: "1.5rem", md: "2rem" },
                }}
              >
                About Email Sender Pro
              </Typography>
            </Stack>
            <Typography
              variant="h3"
              fontWeight={700}
              gutterBottom
              sx={{
                letterSpacing: 0.5,
                mb: 3,
                color: "#111827",
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "1.75rem", md: "2.5rem" },
              }}
            >
              Discover the Power Behind Your Emails
            </Typography>
            <Typography
              color="text.secondary"
              sx={{
                mb: 4,
                lineHeight: 1.7,
                maxWidth: 700,
                mx: "auto",
                fontFamily: "'Inter', sans-serif",
                fontSize: { xs: "0.95rem", md: "1.1rem" },
              }}
            >
              Email Sender Pro is a cutting-edge bulk email solution designed to empower businesses and marketers. With seamless Excel integration, rich media support, and advanced personalization, we help you craft and deliver impactful email campaigns with ease.
            </Typography>
          </Box>

          <Divider sx={{ mb: 4, borderColor: "rgba(102, 126, 234, 0.2)" }} />

          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h5"
              fontWeight={700}
              gutterBottom
              sx={{ fontFamily: "'Inter', sans-serif", color: "#1f2937", fontSize: { xs: "1.25rem", md: "1.5rem" } }}
            >
              How It Works
            </Typography>
            <List>
              <ListItem sx={{ "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.05)" }, transition: "all 0.2s ease" }}>
                <ListItemIcon>
                  <UploadIcon sx={{ color: "#667eea" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Upload Your Excel File"
                  secondary="Easily upload .xlsx or .xls files with recipient data, including Name, Email, and Mobile Number columns."
                  sx={{
                    "& .MuiListItemText-primary": { fontFamily: "'Inter', sans-serif", fontWeight: 500, color: "#374151" },
                    "& .MuiListItemText-secondary": { fontFamily: "'Inter', sans-serif", color: "#6b7280" },
                  }}
                />
              </ListItem>
              <ListItem sx={{ "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.05)" }, transition: "all 0.2s ease" }}>
                <ListItemIcon>
                  <PersonIcon sx={{ color: "#667eea" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Preview & Refine Recipients"
                  secondary="Review and edit your recipient list with options to add, remove, or modify details."
                  sx={{
                    "& .MuiListItemText-primary": { fontFamily: "'Inter', sans-serif", fontWeight: 500, color: "#374151" },
                    "& .MuiListItemText-secondary": { fontFamily: "'Inter', sans-serif", color: "#6b7280" },
                  }}
                />
              </ListItem>
              <ListItem sx={{ "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.05)" }, transition: "all 0.2s ease" }}>
                <ListItemIcon>
                  <CodeIcon sx={{ color: "#667eea" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Craft with Templates"
                  secondary="Select from professional templates or use our rich text editor to create custom emails."
                  sx={{
                    "& .MuiListItemText-primary": { fontFamily: "'Inter', sans-serif", fontWeight: 500, color: "#374151" },
                    "& .MuiListItemText-secondary": { fontFamily: "'Inter', sans-serif", color: "#6b7280" },
                  }}
                />
              </ListItem>
              <ListItem sx={{ "&:hover": { backgroundColor: "rgba(102, 126, 234, 0.05)" }, transition: "all 0.2s ease" }}>
                <ListItemIcon>
                  <SendIcon sx={{ color: "#667eea" }} />
                </ListItemIcon>
                <ListItemText
                  primary="Send Personalized Emails"
                  secondary="Leverage placeholders like [Customer Name] and [Mobile Number] for tailored bulk sending."
                  sx={{
                    "& .MuiListItemText-primary": { fontFamily: "'Inter', sans-serif", fontWeight: 500, color: "#374151" },
                    "& .MuiListItemText-secondary": { fontFamily: "'Inter', sans-serif", color: "#6b7280" },
                  }}
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ mb: 4, borderColor: "rgba(102, 126, 234, 0.2)" }} />

          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              gutterBottom
              sx={{ fontFamily: "'Inter', sans-serif", color: "#1f2937", fontSize: { xs: "1.25rem", md: "1.5rem" } }}
            >
              Key Features
            </Typography>
            <Stack spacing={3}>
              {[
                { title: "📊 Excel Integration", desc: "Supports multi-column Excel uploads for name, email, and mobile data with easy import tools." },
                { title: "🎨 Professional Templates", desc: "Customizable templates for Business, Newsletter, Announcement, and Marketing campaigns." },
                { title: "👤 Personalization", desc: "Dynamic placeholders for personalized emails tailored to each recipient." },
                { title: "🖼️ Image Management", desc: "Upload, resize (Small to Extra Large), and embed images as attachments for reliable delivery." },
                { title: "🎥 Video Integration", desc: "Embed YouTube, Vimeo, and more with clickable thumbnails in emails." },
                { title: "📧 Bulk Sending", desc: "Track progress and get detailed reports for large-scale email campaigns." },
              ].map((feature, index) => (
                <Box key={index} sx={{ "&:hover": { transform: "translateX(10px)" }, transition: "all 0.3s ease" }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    color="primary"
                    sx={{ fontFamily: "'Inter', sans-serif", fontSize: { xs: "1rem", md: "1.125rem" } }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ fontFamily: "'Inter', sans-serif", lineHeight: 1.6, fontSize: { xs: "0.9rem", md: "1rem" } }}
                  >
                    {feature.desc}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Paper>
      </Fade>
    </Box>
  );
}